import { useCallback, useEffect, useState } from "react";
import { fetchCoachClientBilling } from "../lib/stripe.js";
import { useCoachBilling } from "../context/CoachBillingContext.jsx";

export function useCoachClientBilling() {
  const { connect, connectLoading } = useCoachBilling();
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [error, setError] = useState(null);

  const connectReady = Boolean(connect?.ready);
  const connectAccountId = connect?.accountId ?? null;

  const refetch = useCallback(async () => {
    if (!connectReady || !connectAccountId) {
      setPurchases([]);
      setError(null);
      setPurchasesLoading(false);
      return;
    }

    setPurchasesLoading(true);
    setError(null);
    try {
      const data = await fetchCoachClientBilling();
      setPurchases(Array.isArray(data.purchases) ? data.purchases : []);
    } catch (e) {
      setPurchases([]);
      setError(e?.message ?? String(e));
    } finally {
      setPurchasesLoading(false);
    }
  }, [connectReady, connectAccountId]);

  useEffect(() => {
    if (connectLoading) return;

    if (!connectReady || !connectAccountId) {
      setPurchases([]);
      setError(null);
      setPurchasesLoading(false);
      return;
    }

    let cancelled = false;
    setPurchasesLoading(true);
    setError(null);

    fetchCoachClientBilling()
      .then((data) => {
        if (!cancelled) {
          setPurchases(Array.isArray(data.purchases) ? data.purchases : []);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setPurchases([]);
          setError(e?.message ?? String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setPurchasesLoading(false);
      });

    return () => {
      cancelled = true;
      setPurchasesLoading(false);
    };
  }, [connectLoading, connectReady, connectAccountId]);

  return {
    purchases,
    isLoading: connectLoading || purchasesLoading,
    error,
    refetch,
    connectReady,
  };
}
